class InstanceSwiper extends HTMLElement {
  constructor() {
    super();
    this.handle = this.getAttribute('handle');
    this.swiperInitialized = false;
  }

  static get observedAttributes() {
    return ['handle'];
  }

  init() {
    if (this.swiperInitialized) return;

    if (typeof Swiper === 'undefined') {
      console.warn('Swiper is not loaded yet.');
      return;
    }

    const swiperSelector = '.swiper--' + this.handle;
    const swiperElement = document.querySelector(swiperSelector);

    if (!swiperElement) {
  return;
}

    this.swiperInitialized = true;

    this.options = this.setOptions();

    try {
      this.instance = new Swiper(swiperSelector, this.options);
      this.setInteractions();
    } catch (error) {
      console.error('Swiper initialization failed:', error);
      this.swiperInitialized = false;
    }
  }

  connectedCallback() {
    if (Shopify.designMode) {
      window.addEventListener('shopify:section:load', () => this.init());
      window.addEventListener('shopify:section:select', () => this.init());

      setTimeout(() => {
        if (!this.swiperInitialized) this.init();
      }, 100);
    } else {
      window.addEventListener('load', () => this.init());
    }
  }

  disconnectedCallback() {
    if (this.instance && typeof this.instance.destroy === 'function') {
      this.instance.destroy();
    }
  }

  setOptions() {
    this._options = this.getOptionsAsJsonScripts();
    return this._options;
  }

  getOptionsAsJsonScripts() {
    const jsonScriptAutomated = this.querySelector(`script#swiper--${this.handle}--automated-options`);
    const jsonScriptOverwrite = this.querySelector(`script#swiper--${this.handle}--overwrite-options`);

    let options = {};

    const parseJSON = scriptElement => {
      if (!scriptElement || !scriptElement.textContent) return {};

      try {
        return JSON.parse(scriptElement.textContent);
      } catch (error) {
        console.error('Error parsing Swiper JSON options:', error, scriptElement.textContent);
        return {};
      }
    };

    options = { ...options, ...parseJSON(jsonScriptAutomated) };
    options = { ...options, ...parseJSON(jsonScriptOverwrite) };

    return options;
  }

  getThumbsInstance() {
    if (!this.isThumbsActive) return;
    return document.querySelector(`[handle="${this.getAttribute('thumbs')}"]`)?.instance;
  }

  setInteractions() {}
}

class InstanceSwiperProductThumbs extends InstanceSwiper {
  constructor() {
    super();
  }

  setOptions() {
    super.setOptions();
    this.setCenteredSlides();
    return this._options;
  }

  setCenteredSlides() {
    if (this.handle !== 'product-thumbs') return;

    const afterInit = swiper => {
      if (!swiper || !swiper.wrapperEl) return;

      if (swiper.activeIndex !== 0 && typeof swiper.slideTo === 'function') {
        swiper.slideTo(0);
      }

      const swiperWrapper = swiper.wrapperEl;

      const observer = new MutationObserver(() => {
        if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform &&
          swiperWrapper.style.transform !== 'translate3d(0px, 0px, 0px)'
        ) {
          swiperWrapper.style.transform = 'translate3d(0px, 0px, 0px)';
        } else if (
          swiperWrapper.hasAttribute('style') &&
          swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)'
        ) {
          setTimeout(() => {
            if (swiperWrapper.style?.transform === 'translate3d(0px, 0px, 0px)') {
              observer.disconnect();
            }
          }, 500);
        }
      });

      observer.observe(swiperWrapper, { attributes: true });

      const setThumbsWrapperHeightToAuto = () => {
        const firstSlide = this.querySelector('.swiper-slide');
        const allSlides = this.querySelectorAll('.swiper-slide');
        const thumbsWrapper = this.parentElement;

        if (!firstSlide || !allSlides.length || !thumbsWrapper) return;

        const slideHeight = firstSlide.clientHeight;
        if (!slideHeight) return;

        const calculateAllThumbHeight = (slideHeight + 16) * allSlides.length;

        if (thumbsWrapper.offsetHeight > calculateAllThumbHeight) {
          thumbsWrapper.style.height = calculateAllThumbHeight - 16 + 'px';
        } else {
          thumbsWrapper.removeAttribute('style');
        }
      };

      window.addEventListener('resize', setThumbsWrapperHeightToAuto);

      setTimeout(() => {
        setThumbsWrapperHeightToAuto();
      }, 2500);
    };

    this._options = {
      ...this._options,
      on: {
        ...(this._options.on || {}),
        afterInit: afterInit
      }
    };
  }
}

if (!customElements.get('swiper-product-thumbs')) {
  customElements.define('swiper-product-thumbs', InstanceSwiperProductThumbs);
}

class InstanceSwiperProductGallery extends InstanceSwiper {
  constructor() {
    super();
    this.isThumbsActive = this.hasAttribute('thumbs');
    this.isZoomActive = this.hasAttribute('zoom');
    this.modelViewerBtn = this.querySelector('.model-viewer-btn');
    this.modelViewer = this.querySelector('model-viewer');
    this.isModelActive = false;
  }

  static get observedAttributes() {
    return ['thumbs', 'zoom'];
  }

  setOptions() {
    super.setOptions();
    this.setThumbOptions();
    return this._options;
  }

  setThumbOptions() {
    if (!this.isThumbsActive) return;

    const thumbsInstance = super.getThumbsInstance();

    this._options = {
      ...this._options,
      thumbs: {
        swiper: thumbsInstance
      }
    };
  }

  setInteractions() {
    if (!this.instance || typeof this.instance.on !== 'function') return;

    if (this.isThumbsActive) this.setThumbsInteraction();
    if (this.isZoomActive) this.photoSwipeLightboxInit();

    this.initModelViewer();

    const thumbSlides = document.querySelectorAll('.swiper-slide[data-media-type="model"]');
    thumbSlides.forEach(slide => {
      if (slide && typeof slide.addEventListener === 'function') {
        slide.addEventListener('click', () => this.enableSwiper());
      }
    });

    this.instance.on('slideChange', swiper => {
      if (
        window.innerWidth < 750 &&
        swiper?.pagination &&
        typeof swiper.pagination.update === 'function'
      ) {
        swiper.pagination.update();
      }
    });

    this.instance.on('scroll', swiper => {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        if (
          swiper?.pagination &&
          typeof swiper.pagination.update === 'function'
        ) {
          swiper.pagination.update();
        }
      }, 200);
    });
  }

  initModelViewer() {
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    this.resetAllModelViewers();

    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const container = slide.querySelector('.model-viewer-container');
      const modelViewer = container?.querySelector('model-viewer');

      if (btn && container && modelViewer) {
        btn.modelViewer = modelViewer;
        modelViewer.controlButton = btn;

        container.addEventListener('click', e => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
            btn.click();
          }
        });

        btn.addEventListener('click', e => {
          e.preventDefault();
          this.toggleModelViewer(btn, modelViewer);
        });

        modelViewer.addEventListener('mousedown', e => {
          if (!btn.classList.contains('is-active')) {
            e.preventDefault();
            e.stopPropagation();
          }
        });
      }
    });

    if (this.instance?.navigation) {
      const { nextEl, prevEl } = this.instance.navigation;

      const bindNavigationClick = el => {
        if (!el) return;

        const elements = el instanceof NodeList || Array.isArray(el) ? el : [el];

        elements.forEach(button => {
          if (!button || typeof button.addEventListener !== 'function') return;

          button.addEventListener('click', () => {
            this.resetAllModelViewers();
            this.enableSwiper(true);
          });
        });
      };

      bindNavigationClick(nextEl);
      bindNavigationClick(prevEl);
    }
  }

  resetAllModelViewers() {
    const modelSlides = this.querySelectorAll('.swiper-slide[data-media-type="model"]');

    modelSlides.forEach(slide => {
      const btn = slide.querySelector('.model-viewer-btn');
      const modelViewer = slide.querySelector('model-viewer');

      if (btn && modelViewer) {
        btn.classList.remove('is-active');
        modelViewer.interactionPrompt = 'none';
        modelViewer.cameraControls = false;
      }
    });

    this.isModelActive = false;

    if (this.instance) {
      this.instance.allowTouchMove = true;
    }
  }

  toggleModelViewer(btn, modelViewer) {
    if (!btn || !modelViewer || !this.instance) return;

    const wasActive = btn.classList.contains('is-active');

    this.resetAllModelViewers();

    if (!wasActive) {
      this.isModelActive = true;
      this.instance.allowTouchMove = false;

      modelViewer.interactionPrompt = 'auto';
      modelViewer.cameraControls = true;
      btn.classList.add('is-active');
    }
  }

  temporarilyDisableSwiper() {
    if (!this.instance) return;

    this.instance.allowTouchMove = false;

    this.swiperTimeout = setTimeout(() => {
      if (!this.isModelActive) {
        this.enableSwiper();
      }
    }, 100);
  }

  enableSwiper(force = false) {
    if (this.swiperTimeout) {
      clearTimeout(this.swiperTimeout);
    }

    if (!this.instance) return;

    if (force || !this.isModelActive) {
      this.instance.allowTouchMove = true;

      if (force && this.isModelActive) {
        this.isModelActive = false;

        if (this.modelViewer) {
          this.modelViewer.interactionPrompt = 'none';
          this.modelViewer.cameraControls = false;
        }

        if (this.modelViewerBtn) {
          this.modelViewerBtn.classList.remove('is-active');
          this.modelViewerBtn.classList.remove('btn--active');
        }
      }
    }
  }

  setThumbsInteraction() {
    if (!this.isThumbsActive || !this.instance) return;

    const thumbsSwiper = this.options?.thumbs?.swiper;

    if (!thumbsSwiper || !thumbsSwiper.el || typeof thumbsSwiper.el.addEventListener !== 'function') return;

    thumbsSwiper.el.addEventListener('click', () => {
      this.enableSwiper(true);

      if (this.modelViewerBtn) {
        this.modelViewerBtn.classList.remove('is-active');
        this.modelViewerBtn.classList.remove('btn--active');
      }
    });

    this.instance.on('slideChangeTransitionStart', swiper => {
      const activeIndex = swiper?.activeIndex;
      const thumbsActiveIndex = thumbsSwiper?.activeIndex;

      if (
        typeof activeIndex !== 'undefined' &&
        typeof thumbsActiveIndex !== 'undefined' &&
        activeIndex !== thumbsActiveIndex &&
        typeof thumbsSwiper.slideTo === 'function'
      ) {
        thumbsSwiper.slideTo(activeIndex);
      }
    });
  }

  photoSwipeLightboxInit() {
    if (typeof PhotoSwipeLightbox === 'undefined' || typeof PhotoSwipe === 'undefined') return;

    const closeIcon = document.querySelector('[data-close-icon]');
    const prevIcon = document.querySelector('[data-prev-icon]');
    const nextIcon = document.querySelector('[data-next-icon]');

    const isPhone = () => window.innerWidth < 750;

    const photoSwipeLightboxInstance = new PhotoSwipeLightbox({
      gallery: '.photoswipe-wrapper',
      children: 'a.product__gallery-toggle',
      mainClass: 'pswp--product-media-gallery',
      loop: false,
      showHideAnimationType: 'zoom',
      initialZoomLevel: zoomLevelObject => {
        return isPhone() ? zoomLevelObject.vFill : zoomLevelObject.fit;
      },
      secondaryZoomLevel: zoomLevelObject => {
        return isPhone() ? zoomLevelObject.fit : 1;
      },
      pswpModule: PhotoSwipe
    });

    photoSwipeLightboxInstance.addFilter('uiElement', (element, data) => {
      if (data.name === 'close' && closeIcon) {
        element.innerHTML = closeIcon.innerHTML;
      } else if (data.name === 'arrowPrev' && prevIcon) {
        element.innerHTML = prevIcon.innerHTML;
      } else if (data.name === 'arrowNext' && nextIcon) {
        element.innerHTML = nextIcon.innerHTML;
      }

      return element;
    });

    photoSwipeLightboxInstance.addFilter('itemData', itemData => {
      if (itemData.type === 'html' && itemData.element) {
        return {
          html: itemData.element.dataset.pswpHtml
        };
      }

      return itemData;
    });

    photoSwipeLightboxInstance.init();

    photoSwipeLightboxInstance.on('beforeOpen', () => {
      document.body.classList.add('overflow-hidden');

      const videos = this.querySelectorAll('video');

      Array.from(videos).forEach(video => {
        if (!video || typeof video.play !== 'function') return;

        video
          .play()
          .then(() => {})
          .catch(() => {
            video.pause();
          });
      });
    });

    photoSwipeLightboxInstance.on('closingAnimationStart', () => {
      document.body.classList.remove('overflow-hidden');
    });
  }

  setActiveMedia(id) {
    const mediaFound = Array.from(this.querySelectorAll('[data-media-id]')).find(
      media => Number(media.dataset.mediaId) === id
    );

    if (!mediaFound) return;

    if (this.instance && typeof this.instance.slideTo === 'function') {
      if (this.dataset.hideOtherVariantsMedia === 'false') {
        this.instance.slideTo(Number(mediaFound.dataset.index));
      }
    }
  }
}

if (!customElements.get('swiper-product-gallery')) {
  customElements.define('swiper-product-gallery', InstanceSwiperProductGallery);
}

class ProductMediaInfo extends HTMLElement {
  constructor() {
    super();

    this.init();
    window.addEventListener('resize', this.init.bind(this));

    if (Shopify.designMode) {
      window.addEventListener('shopify:section:load', this.init.bind(this));
    }
  }

  init() {
    let containerOffsetWidth = document.querySelector('.main-product__media--slider')?.offsetWidth || 330;

    const gridItem = document.querySelector('.main-product__media--grid-item');

    if (gridItem && window.innerWidth > 750) {
      containerOffsetWidth = gridItem.offsetWidth;
    }

    const maxWidthForInfo = (containerOffsetWidth - 48) / 2;

    const lastP = this.querySelector('p:last-child');
    const mediaInfoHidden = this.querySelector('p[aria-hidden]');

    if (!lastP || !mediaInfoHidden) return;

    const mediaInfoTextWidth = lastP.offsetWidth;

    if (mediaInfoTextWidth > maxWidthForInfo) {
      this.classList.remove('animation-stopped');
      this.style.cssText = `--marquee-speed: ${(mediaInfoTextWidth / maxWidthForInfo) * 8}s`;
      mediaInfoHidden.style.display = '';
    } else {
      this.classList.add('animation-stopped');
      this.style.cssText = '';
      mediaInfoHidden.style.display = 'none';
    }
  }
}

if (!customElements.get('product-media-info')) {
  customElements.define('product-media-info', ProductMediaInfo);
}